import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconCopy,
  IconShieldCheck,
  IconShieldLock,
  IconAlertTriangle,
} from "@tabler/icons-react";
import QRCode from "qrcode";
import { Alert, Badge, Button, Group, Heading, OtpInput, Paper, Skeleton, Stack, Text } from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { disable2FA, enable2FA, setup2FA } from "@/shared/api/auth.api";
import type { Setup2FA } from "@/lib/types";
import { seguridad2FAQueryOptions } from "../hooks/use-seguridad-queries";

/**
 * Bloque de gestión 2FA (P36 · TOTP Google Authenticator) sin chrome de
 * página: activar (QR + clave manual + primer código), códigos de
 * recuperación (se muestran UNA vez) y desactivar (exige un código vigente).
 * Vive en la pestaña Seguridad de Mi perfil (P40) — la página /seguridad
 * dedicada y su item del menú de usuario se retiraron a pedido del usuario.
 */
export function SeccionDosFactores() {
  const qc = useQueryClient();
  const estado = useQuery(seguridad2FAQueryOptions);

  const invalidar = () => void qc.invalidateQueries({ queryKey: qk.seguridad2FA });

  // Skeleton mientras llega el estado (en vez de `null`, que dejaba el bloque
  // en blanco hasta que respondía el servidor · con la precarga del loader de
  // /perfil normalmente ya está en caché y ni se ve).
  if (estado.isPending) {
    return (
      <Paper padding="lg">
        <Stack gap="sm">
          <Skeleton height={16} width="40%" />
          <Skeleton height={12} width="75%" />
          <Skeleton height={38} width={180} className="mt-1" />
        </Stack>
      </Paper>
    );
  }

  return estado.data?.habilitado ? (
    <DosFactoresActivo onDesactivado={invalidar} />
  ) : (
    <ActivarDosFactores onActivado={invalidar} />
  );
}

function ActivarDosFactores({ onActivado }: { onActivado: () => void }) {
  const [setup, setSetup] = useState<Setup2FA | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [recuperacion, setRecuperacion] = useState<string[] | null>(null);

  const iniciar = useMutation({
    mutationFn: setup2FA,
    onSuccess: setSetup,
    onError: toastErrorApi,
  });

  // El QR se genera EN el navegador desde la URI otpauth:// · el secreto
  // nunca pasa por un servicio externo de QR.
  useEffect(() => {
    if (!setup) return;
    void QRCode.toDataURL(setup.otpauthUri, { width: 220, margin: 1 }).then(setQr);
  }, [setup]);

  const activar = useMutation({
    mutationFn: () => enable2FA(codigo),
    onSuccess: (r) => {
      setRecuperacion(r.codigosRecuperacion);
      toastExito({
        title: "Verificación en dos pasos activada",
        message: "Guarda tus códigos de recuperación · se muestran solo una vez.",
      });
    },
    onError: (e) => {
      setCodigo("");
      toastErrorApi(e);
    },
  });

  // Paso 3 · códigos de recuperación (tras activar).
  if (recuperacion) {
    return (
      <Paper padding="lg">
        <Stack gap="md">
          <Group gap="xs">
            <IconShieldCheck size={20} color="var(--color-exito-600)" />
            <Heading order={4}>Códigos de recuperación</Heading>
          </Group>
          <Alert variant="warning" icon={<IconAlertTriangle size={16} />}>
            Guárdalos en un lugar seguro. Cada código funciona UNA sola vez y son la
            única forma de entrar si pierdes tu dispositivo. No volverán a mostrarse.
          </Alert>
          <div className="grid grid-cols-2 gap-2">
            {recuperacion.map((c) => (
              <code
                key={c}
                className="block rounded-md border border-border bg-muted/40 py-1.5 text-center font-mono text-sm"
              >
                {c}
              </code>
            ))}
          </div>
          <Group justify="space-between">
            <CopyButton value={recuperacion.join("\n")}>
              {({ copied, copy }) => (
                <Button variant="default" leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? "Copiados" : "Copiar todos"}
                </Button>
              )}
            </CopyButton>
            <Button onClick={onActivado}>Listo, los guardé</Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  // Paso 1 · aún sin setup.
  if (!setup) {
    return (
      <Paper padding="lg">
        <Stack gap="md">
          <Group gap="xs">
            <IconShieldLock size={20} color="var(--color-multiva-600)" />
            <Heading order={4}>Verificación en dos pasos</Heading>
            <Badge size="sm" color="slate">
              Desactivada
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            Necesitarás una app de autenticación en tu teléfono, como{" "}
            <Text span fw={600}>
              Google Authenticator
            </Text>{" "}
            (también funciona Microsoft Authenticator, Authy o 1Password).
          </Text>
          <Button onClick={() => iniciar.mutate()} loading={iniciar.isPending} className="max-w-55">
            Configurar
          </Button>
        </Stack>
      </Paper>
    );
  }

  // Paso 2 · QR + clave + primer código.
  return (
    <Paper padding="lg">
      <Stack gap="md">
        <Heading order={4}>Escanea el código QR</Heading>
        <Text size="sm" c="dimmed">
          Abre Google Authenticator, toca “+” y escanea este código. Si no puedes
          escanear, captura la clave manualmente.
        </Text>
        <div className="flex justify-center">
          {qr ? <img src={qr} alt="Código QR del autenticador" width={220} height={220} /> : null}
        </div>
        <Group justify="center" gap="xs">
          <Text size="xs" c="dimmed">
            Clave manual:
          </Text>
          <code className="font-mono text-sm">{setup.claveCompartida}</code>
          <CopyButton value={setup.claveCompartida.replace(/\s/g, "")}>
            {({ copied, copy }) => (
              <Button size="xs" variant="subtle" onClick={copy}>
                {copied ? "Copiada" : "Copiar"}
              </Button>
            )}
          </CopyButton>
        </Group>
        <Stack gap={6} align="center">
          <Text size="sm" fw={500}>
            Ingresa el código que muestra la app para confirmar
          </Text>
          <OtpInput length={6} value={codigo} onChange={setCodigo} onComplete={() => activar.mutate()} />
        </Stack>
        <Button
          fullWidth
          disabled={codigo.length !== 6}
          loading={activar.isPending}
          onClick={() => activar.mutate()}
        >
          Activar verificación en dos pasos
        </Button>
      </Stack>
    </Paper>
  );
}

function DosFactoresActivo({ onDesactivado }: { onDesactivado: () => void }) {
  const [codigo, setCodigo] = useState("");

  const desactivar = useMutation({
    mutationFn: () => disable2FA(codigo),
    onSuccess: () => {
      toastExito({
        title: "Verificación en dos pasos desactivada",
        message: "Tu cuenta vuelve a protegerse solo con contraseña.",
      });
      onDesactivado();
    },
    onError: (e) => {
      setCodigo("");
      toastErrorApi(e);
    },
  });

  return (
    <Paper padding="lg">
      <Stack gap="md">
        <Group gap="xs">
          <IconShieldCheck size={20} color="var(--color-exito-600)" />
          <Heading order={4}>Verificación en dos pasos</Heading>
          <Badge size="sm" color="exito">
            Activada
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Tu cuenta pide un código del autenticador en cada inicio de sesión. Si
          pierdes el dispositivo, usa uno de tus códigos de recuperación.
        </Text>
        <Alert variant="warning" icon={<IconAlertTriangle size={16} />}>
          Para desactivarla (o cambiar de dispositivo) ingresa un código vigente de tu
          app. Al desactivar, el QR anterior deja de servir y los códigos de
          recuperación se invalidan.
        </Alert>
        <Stack gap={6} align="center">
          <OtpInput length={6} value={codigo} onChange={setCodigo} />
          <Button
            variant="light"
            className="bg-peligro-50 text-peligro-800 hover:bg-peligro-100 dark:bg-peligro-900/40 dark:text-peligro-200"
            disabled={codigo.length !== 6}
            loading={desactivar.isPending}
            onClick={() => desactivar.mutate()}
          >
            Desactivar verificación en dos pasos
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Reemplazo local de `<CopyButton>` de Mantine (render-props: expone
 * `{copied, copy}`) · sin equivalente en `components/ui` todavía (un solo
 * consumidor). `copied` se resetea solo tras 1.5s, igual que el original.
 */
function CopyButton({
  value,
  children,
}: {
  value: string;
  children: (state: { copied: boolean; copy: () => void }) => React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return <>{children({ copied, copy })}</>;
}
